import { Layout, SlideoutDecorator } from "@mcleod/components";
import { ModelRow, StringUtil } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";
import JSZip from 'jszip';
import { ImportSnapshot } from "./ImportSnapshot";

export interface ControlFileInfo {
  fileNames: string[];
  fileDescrs: string[];
  idFieldNames: string[];
  parentRecordId: string;
  parentRecordTableName: string;
}

export interface DirectoryContent {
  templateFiles: { name: string, content: string, isText: boolean }[];
  commControlFiles: { name: string, content: string }[];
  // Specific event file contents (from events directory)
  ediDataRuleEventContents: string;
  ediReqdfldgroupEventContents: string;
  ediRequiredFieldEventContents: string;
  edischeduleContents: string;
  ediStatusEventContents: string;
  // Specific data validation rule file contents (from dataValidationRules directory)
  ediDataRuleContents: string;
  ediReqdfldgroupContents: string;
  ediRequiredFieldContents: string;
}

export class ImportSnapshotManager {

  /**
   * Handles the zip file upload and creates the import snapshot dialog
   * @param file The zip file to process
   * @param onProfileOpenCallback Optional callback to open a profile after import completion
   * @returns Promise containing parentRecordTableName and parentRecordId
   */
  public async handleZipFileUpload(
    file: File,
    onProfileOpenCallback?: (parentRecordTableName: string, parentRecordId: string) => void
  ): Promise<{ parentRecordTableName: string, parentRecordId: string }> {
    try {
      console.log('Selected zip file:', file.name, 'Size:', file.size, 'bytes');

      // Read the zip file
      const zip = new JSZip();
      const zipData = await zip.loadAsync(file);

      // Look for controlFile.txt
      let controlFileText: string | null = null;
      const controlFile = zipData.file("controlFile.txt");
      if (controlFile) {
        controlFileText = await controlFile.async('text');
      }

      // Parse control file if it exists
      const controlFileInfo = this.parseControlFileText(controlFileText);

      // Create ImportSnapshot layout
      const layout = Layout.getLayout("lme/datafusion/ImportSnapshot") as ImportSnapshot;
      layout.snapshotFilename = file.name;
      layout.addLayoutLoadListener(() => {
        // Set the callback on the layout if provided
        if (onProfileOpenCallback && controlFileInfo.parentRecordTableName && controlFileInfo.parentRecordId) {
          layout.setOnImportCompleteCallback(onProfileOpenCallback);
        }

        // Populate the table with zip file contents after the layout is loaded
        this.populateImportTable(layout, zipData, controlFileInfo);

        // Create SlideoutDecorator and open the slideout
        const slideoutDecorator = new SlideoutDecorator({
          layout: layout,
          title: `Select Profile Data For Import - ${file.name}`,
          width: '50%',
          fillVerticalSpace: true,
          onClose: (cancelled: boolean) => {
            if (!cancelled) {
              // Handle the import logic here if needed
              console.log("User confirmed import");
              // You can access the selected items from layout.tableImport
            }
            layout.slideOut();
          }
        });
      });

      // Return the parent record information from the control file
      return {
        parentRecordTableName: controlFileInfo.parentRecordTableName,
        parentRecordId: controlFileInfo.parentRecordId
      };

    } catch (error) {
      console.error('Error processing zip file:', error);
      await CommonDialogs.showError(`Failed to process zip file: ${error.message || error}`);
      // Return empty values in case of error
      return {
        parentRecordTableName: '',
        parentRecordId: ''
      };
    }
  }

  /**
   * Parses the control file text and extracts file information
   */
  private parseControlFileText(text: string | null): ControlFileInfo {
    const result: ControlFileInfo = {
      fileNames: [],
      fileDescrs: [],
      idFieldNames: [],
      parentRecordId: '',
      parentRecordTableName: ''
    };

    let parentRecordId = '';
    let parentRecordTableName = '';

    if (!text) {
      return result;
    }

    const lines = text.split('\n');

    for (const line of lines) {
      if (line.trim()) {
        const elements = line.split('|');

        // Always process the line if it has at least one element
        if (elements.length >= 1) {
          const fileName = elements[0] || '';
          const fileDescr = elements.length >= 2 ? (elements[1] || fileName) : fileName;
          const idFieldName = elements.length >= 5 ? (elements[4] || '') : '';

          result.fileNames.push(fileName);
          result.fileDescrs.push(fileDescr);
          result.idFieldNames.push(idFieldName);

          // Extract parent table name from the first element and strip .unl extension
          if (!parentRecordTableName && fileName) {
            parentRecordTableName = fileName.replace(/\.unl$/, '');
            result.parentRecordTableName = parentRecordTableName;
          }

          // Extract parent record ID from the 4th element if present
          if (elements.length >= 4) {
            parentRecordId = !StringUtil.isEmptyString(elements[3]) ? elements[3] : parentRecordId;
            result.parentRecordId = parentRecordId;
          }
        }
      }
    }

    return result;
  }

  /**
   * Populates the import table with data from the zip file
   */
  private async populateImportTable(importSnapshot: ImportSnapshot, zipData: JSZip, controlFileInfo: ControlFileInfo): Promise<void> {
    // Clear existing table data
    if (importSnapshot.tableImport.dataSource) {
      importSnapshot.tableImport.dataSource.clear();
    }

    // If we have control file info, use it to populate the table
    if (controlFileInfo.fileNames.length > 0) {
      // Collect all files from directories organized by directory name
      const directoryContents = await this.collectDirectoryContents(zipData, controlFileInfo);

      // Now process each row individually
      for (let i = 0; i < controlFileInfo.fileNames.length; i++) {
        const fileName = controlFileInfo.fileNames[i];
        const fileDescr = controlFileInfo.fileDescrs[i] || fileName;
        const idFieldName = controlFileInfo.idFieldNames[i] || '';
        const parentRecordId = controlFileInfo.parentRecordId;
        const parentRecordTableName = controlFileInfo.parentRecordTableName;

        // Check if file exists in zip
        const zipFile = zipData.file(fileName);
        let commControlContents = '';
        let ediTemplateContents = '';
        let ediTemplateTextContents = '';
        let ediDataRuleEventContents = '';
        let ediReqdfldgroupEventContents = '';
        let ediRequiredFieldEventContents = '';
        let edischeduleContents = '';
        let ediStatusEventContents = '';
        let ediDataRuleContents = '';
        let ediReqdfldgroupContents = '';
        let ediRequiredFieldContents = '';

        // Get content from the specific directory that matches this fileName
        const directoryContent = directoryContents.get(fileName);
        console.log(`Looking for directory: ${fileName}, found: ${directoryContent ? 'YES' : 'NO'}`);

        if (directoryContent) {
          // Process comm control, template, and event file assignments
          const contentResults = this.processDirectoryContent(
            directoryContent,
            fileDescr,
            fileName,
            i
          );

          commControlContents = contentResults.commControlContents;
          ediTemplateContents = contentResults.ediTemplateContents;
          ediTemplateTextContents = contentResults.ediTemplateTextContents;

          // Handle specific event file contents
          ediDataRuleEventContents = contentResults.ediDataRuleEventContents;
          ediReqdfldgroupEventContents = contentResults.ediReqdfldgroupEventContents;
          ediRequiredFieldEventContents = contentResults.ediRequiredFieldEventContents;
          edischeduleContents = contentResults.edischeduleContents;
          ediStatusEventContents = contentResults.ediStatusEventContents;

          // Handle specific data validation rule file contents
          ediDataRuleContents = contentResults.ediDataRuleContents;
          ediReqdfldgroupContents = contentResults.ediReqdfldgroupContents;
          ediRequiredFieldContents = contentResults.ediRequiredFieldContents;
        } else {
          console.log(`No directory content found for: ${fileName}`);
          console.log('Available directories:', Array.from(directoryContents.keys()));
        }

        console.log(`Row ${i} (${fileDescr}, fileName: ${fileName}): Regular template length: ${ediTemplateContents.length}, Text template length: ${ediTemplateTextContents.length}`);
        console.log(`  Event files - DataRule: ${ediDataRuleEventContents.length}, ReqFldGroup: ${ediReqdfldgroupEventContents.length}, ReqField: ${ediRequiredFieldEventContents.length}, Schedule: ${edischeduleContents.length}, StatusEvent: ${ediStatusEventContents.length}`);
        console.log(`  Data validation rules - DataRule: ${ediDataRuleContents.length}, ReqFldGroup: ${ediReqdfldgroupContents.length}, ReqField: ${ediRequiredFieldContents.length}`);

        const fileContents = zipFile ? await zipFile.async('string') : '';
        this.addTableRow(
          importSnapshot,
          false,
          fileDescr,
          false,
          fileName,
          idFieldName,
          fileContents,
          commControlContents,
          ediTemplateContents,
          ediTemplateTextContents,
          '', // eventContents - not used
          ediDataRuleEventContents,
          ediReqdfldgroupEventContents,
          ediRequiredFieldEventContents,
          edischeduleContents,
          ediStatusEventContents,
          ediDataRuleContents,
          ediReqdfldgroupContents,
          ediRequiredFieldContents,
          parentRecordId,
          parentRecordTableName
        );
      }
    } else {
      CommonDialogs.showError('Control file is missing or invalid in the zip archive. Cannot populate import table.', 'Snapshot Import Error');
    }
  }

  /**
   * Collects directory contents from the zip file
   */
  private async collectDirectoryContents(zipData: JSZip, controlFileInfo: ControlFileInfo): Promise<Map<string, DirectoryContent>> {
    const directoryContents = new Map<string, DirectoryContent>();

    for (let i = 0; i < controlFileInfo.fileNames.length; i++) {
      const fileName = controlFileInfo.fileNames[i];
      const zipFile = zipData.file(fileName);

      if (!zipFile) {
        // This is a directory, collect template and comm control files
        const directory = zipData.folder(fileName);
        if (directory) {
          const templateFiles: { name: string, content: string, isText: boolean }[] = [];
          const commControlFiles: { name: string, content: string }[] = [];

          // Initialize specific event file contents (from events directory)
          let ediDataRuleEventContents = '';
          let ediReqdfldgroupEventContents = '';
          let ediRequiredFieldEventContents = '';
          let edischeduleContents = '';
          let ediStatusEventContents = '';

          // Initialize specific data validation rule file contents (from dataValidationRules directory)
          let ediDataRuleContents = '';
          let ediReqdfldgroupContents = '';
          let ediRequiredFieldContents = '';

          for (const [relativePath, file] of Object.entries(directory.files)) {
            if (!file.dir) {
              const fileContent = await file.async('string');
              const fullFileName = relativePath.toLowerCase();

              // Only include files that are actually in THIS directory (not subdirectories)
              const pathParts = relativePath.split('/').filter(part => part.length > 0);
              const isDirectFile = pathParts.length === 1; // File is directly in this directory

              if (isDirectFile) {
                if (fullFileName.includes('commcontrol') || fullFileName.includes('comm_control')) {
                  commControlFiles.push({
                    name: relativePath,
                    content: fileContent
                  });
                } else if (fullFileName.includes('template')) {
                  const isText = fullFileName.includes('.txt') || fullFileName.includes('text');
                  templateFiles.push({
                    name: relativePath,
                    content: fileContent,
                    isText: isText
                  });
                } else if (fullFileName.toLowerCase().includes('events')) {
                  // Handle event files - .unl files in the events directory
                  if (fullFileName.includes('edi_data_rule')) {
                    ediDataRuleEventContents = fileContent;
                  } else if (fullFileName.includes('edi_reqdfldgroup')) {
                    ediReqdfldgroupEventContents = fileContent;
                  } else if (fullFileName.includes('edi_required_field')) {
                    ediRequiredFieldEventContents = fileContent;
                  } else if (fullFileName.includes('edischedule')) {
                    edischeduleContents = fileContent;
                  } else if (fullFileName.includes('edistatusevent')) {
                    ediStatusEventContents = fileContent;
                  }
                } else if (fullFileName.toLowerCase().includes('datavalidationrules')) {
                  // Handle data validation rule files - .unl files in the dataValidationRules directory
                  if (fullFileName.includes('edi_data_rule')) {
                    ediDataRuleContents = fileContent;
                  } else if (fullFileName.includes('edi_reqdfldgroup')) {
                    ediReqdfldgroupContents = fileContent;
                  } else if (fullFileName.includes('edi_required_field')) {
                    ediRequiredFieldContents = fileContent;
                  }
                }
              }
            }
          }

          directoryContents.set(fileName, {
            templateFiles: templateFiles,
            commControlFiles: commControlFiles,
            ediDataRuleEventContents,
            ediReqdfldgroupEventContents,
            ediRequiredFieldEventContents,
            edischeduleContents,
            ediStatusEventContents,
            ediDataRuleContents,
            ediReqdfldgroupContents,
            ediRequiredFieldContents
          });
        }
      }
    }

    return directoryContents;
  }

  /**
   * Processes directory content to assign appropriate template, comm control, and event file content
   */
  private processDirectoryContent(
    directoryContent: DirectoryContent,
    fileDescr: string,
    fileName: string,
    rowIndex: number
  ): {
    commControlContents: string,
    ediTemplateContents: string,
    ediTemplateTextContents: string,
    ediDataRuleEventContents: string,
    ediReqdfldgroupEventContents: string,
    ediRequiredFieldEventContents: string,
    edischeduleContents: string,
    ediStatusEventContents: string,
    ediDataRuleContents: string,
    ediReqdfldgroupContents: string,
    ediRequiredFieldContents: string
  } {

    let commControlContents = '';
    let ediTemplateContents = '';
    let ediTemplateTextContents = '';

    // Debug: list all files in this directory
    console.log(`Directory ${fileName} has ${directoryContent.templateFiles.length} template files, ${directoryContent.commControlFiles.length} comm control files`);
    console.log(`Event file contents - DataRule: ${directoryContent.ediDataRuleEventContents.length}, ReqFldGroup: ${directoryContent.ediReqdfldgroupEventContents.length}, ReqField: ${directoryContent.ediRequiredFieldEventContents.length}, Schedule: ${directoryContent.edischeduleContents.length}, StatusEvent: ${directoryContent.ediStatusEventContents.length}`);
    console.log(`Data validation rule contents - DataRule: ${directoryContent.ediDataRuleContents.length}, ReqFldGroup: ${directoryContent.ediReqdfldgroupContents.length}, ReqField: ${directoryContent.ediRequiredFieldContents.length}`);

    directoryContent.templateFiles.forEach((tf, idx) => {
      console.log(`  Template ${idx}: ${tf.name}, isText: ${tf.isText}, contentLength: ${tf.content.length}`);
    });

    directoryContent.commControlFiles.forEach((cf, idx) => {
      console.log(`  CommControl ${idx}: ${cf.name}, contentLength: ${cf.content.length}`);
    });

    // Only assign comm control if this row description indicates it's a comm control row
    const isCommControlRow = fileDescr.toLowerCase().includes('comm') && fileDescr.toLowerCase().includes('id');

    if (isCommControlRow && directoryContent.commControlFiles.length > 0) {
      // Find comm control that belongs to this specific directory
      const directoryName = fileName.toLowerCase();

      const commControlFile = directoryContent.commControlFiles.find(cf =>
        cf.name.toLowerCase().includes(directoryName) && cf.content.length > 0
      );

      console.log(`Looking for comm control containing "${directoryName}"`);
      console.log(`Found commControlFile: ${commControlFile ? commControlFile.name : 'NO'}`);

      if (commControlFile) {
        commControlContents = commControlFile.content;
      }
    }

    // Only assign template content if this row description indicates it's a template
    const isTemplateRow = fileDescr.toLowerCase().includes('template');
    console.log(`Row ${rowIndex}: fileDescr="${fileDescr}", isTemplateRow: ${isTemplateRow}, isCommControlRow: ${isCommControlRow}`);

    if (isTemplateRow && directoryContent.templateFiles.length > 0) {
      // Find templates that belong to this specific directory
      const directoryName = fileName.toLowerCase();

      const regularTemplate = directoryContent.templateFiles.find(t =>
        !t.isText && t.name.toLowerCase().includes(directoryName) && t.content.length > 0
      );
      const textTemplate = directoryContent.templateFiles.find(t =>
        t.isText && t.name.toLowerCase().includes(directoryName) && t.content.length > 0
      );

      console.log(`Looking for templates containing "${directoryName}"`);
      console.log(`Found regularTemplate: ${regularTemplate ? regularTemplate.name : 'NO'}, textTemplate: ${textTemplate ? textTemplate.name : 'NO'}`);

      if (regularTemplate) {
        ediTemplateContents = regularTemplate.content;
      }
      if (textTemplate) {
        ediTemplateTextContents = textTemplate.content;
      }
    }

    return {
      commControlContents,
      ediTemplateContents,
      ediTemplateTextContents,
      ediDataRuleEventContents: directoryContent.ediDataRuleEventContents,
      ediReqdfldgroupEventContents: directoryContent.ediReqdfldgroupEventContents,
      ediRequiredFieldEventContents: directoryContent.ediRequiredFieldEventContents,
      edischeduleContents: directoryContent.edischeduleContents,
      ediStatusEventContents: directoryContent.ediStatusEventContents,
      ediDataRuleContents: directoryContent.ediDataRuleContents,
      ediReqdfldgroupContents: directoryContent.ediReqdfldgroupContents,
      ediRequiredFieldContents: directoryContent.ediRequiredFieldContents
    };
  }

  /**
   * Adds a row to the import table
   */
  private addTableRow(
    importSnapshot: ImportSnapshot,
    apply: boolean,
    data: string,
    createNewId: boolean,
    fileName?: string,
    idFieldName?: string,
    fileContents?: string,
    commControlContents?: string,
    ediTemplateContents?: string,
    ediTemplateTextContents?: string,
    eventContents?: string,
    ediDataRuleEventContents?: string,
    ediReqdfldgroupEventContents?: string,
    ediRequiredFieldEventContents?: string,
    edischeduleContents?: string,
    ediStatusEventContents?: string,
    ediDataRuleContents?: string,
    ediReqdfldgroupContents?: string,
    ediRequiredFieldContents?: string,
    parentRecordId?: string,
    parentRecordTableName?: string
  ): void {
    // Create the data object for the row with the correct field names matching the layout
    const rowData = {
      apply_change: apply,
      data: data,
      new_id: createNewId ? 'Y' : (createNewId === false ? 'N' : null),
      parent_record_id: parentRecordId || '',
      parent_record_table_name: parentRecordTableName || '',
      contents: fileContents || '',
      comm_control_contents: commControlContents || '',
      edi_template_contents: ediTemplateContents || '',
      edi_template_text_contents: ediTemplateTextContents || '',
      edi_data_rule_event_contents: ediDataRuleEventContents || '',
      edi_reqdfldgroup_event_contents: ediReqdfldgroupEventContents || '',
      edi_required_field_event_contents: ediRequiredFieldEventContents || '',
      edischedule_contents: edischeduleContents || '',
      edistatusevent_contents: ediStatusEventContents || '',
      edi_data_rule_contents: ediDataRuleContents || '',
      edi_reqdfldgroup_contents: ediReqdfldgroupContents || '',
      edi_required_field_contents: ediRequiredFieldContents || '',
      // Store additional metadata that might be needed
      _fileName: fileName,
      _idFieldName: idFieldName
    };

    // Create a ModelRow with a default URL if dataSource URL is not available
    const dataSourceUrl = importSnapshot.tableImport.dataSource?.url || "lme/datafusion/import-snapshot-data";
    const modelRow = new ModelRow(dataSourceUrl, false, rowData);

    // Add row to the table's data source
    importSnapshot.tableImport.dataSource.addRow(modelRow);
  }
}
